import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useRole } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Building2, Users, User, Shield } from "lucide-react";

type Role = "broker" | "employer" | "employee" | "superadmin";

interface RoleSwitcherProps {
  // Restrict which roles can be selected; if omitted, all roles are allowed
  allowedRoles?: Role[];
}

export function RoleSwitcher({ allowedRoles }: RoleSwitcherProps) {
  const queryClient = useQueryClient();
  const { currentRole: roleFromHook, setCurrentRole, user, isLoading } = useRole();
  const [, setLocation] = useLocation();

  // Respect allowedRoles when rendering label/icon, but don't fight useRole's state
  const currentRole: Role =
    allowedRoles && !allowedRoles.includes(roleFromHook as Role)
      ? (allowedRoles[0] ?? "broker")
      : (roleFromHook as Role);

  const switchRole = (role: Role) => {
    try {
      // Sync viewAsEmployee (server/auth.ts flips the effective role to
      // "employee" when it's present) BEFORE flipping the role: the role
      // setter triggers the re-render that issues the next /api/auth/user
      // fetch, and useAuth reads both params from the URL at that moment.
      // Writing viewAsEmployee afterwards let the first fetch go out without
      // it (the portal ping-pong bug).
      const url = new URL(window.location.href);
      if (role === "employee" && user?.employeeId) {
        localStorage.setItem("viewAsEmployee", String(user.employeeId));
        url.searchParams.set("viewAsEmployee", String(user.employeeId));
      } else {
        localStorage.removeItem("viewAsEmployee");
        url.searchParams.delete("viewAsEmployee");
      }
      window.history.replaceState(null, "", url.toString());

      // Update role state + URL + localStorage in one shot (context setter handles all three)
      setCurrentRole(role);

      // Refresh role-scoped caches in the background
      queryClient.invalidateQueries({ queryKey: ["/api/auth/permissions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });

      // If current pathname isn't valid for the new role, nudge to dashboard
      const pathname = window.location.pathname;
      const sharedRoutes = [/^\/clients(\/.*)?$/, /^\/employee(\/.*)?$/, /^\/plans(\/.*)?$/, /^\/enrollments$/, /^\/premiums$/, /^\/tasks$/, /^\/calendar$/];
      const roleAllowed: Record<Role, RegExp[]> = {
        superadmin: [/^\/superadmin(\/.*)?$/],
        broker: [/^\/agency(\/.*)?$/, /^\/clients(\/.*)?$/, /^\/prospects(\/.*)?$/, /^\/quoting$/, /^\/tickets$/, /^\/marketing$/, /^\/directory$/, /^\/reports$/, /^\/commissions$/, /^\/eligibility-milestones$/],
        employer: [/^\/employees(\/.*)?$/, /^\/payroll$/, /^\/pto$/, /^\/contractors$/, /^\/benefits$/, /^\/compliance(\/.*)?$/, /^\/hr$/, /^\/documents$/, /^\/reports$/, /^\/eligibility-milestones$/],
        employee: [/^\/profile$/, /^\/employment$/, /^\/benefits$/, /^\/pto$/, /^\/bank-and-tax$/, /^\/documents$/, /^\/aca-compliance$/, /^\/support$/],
      };
      const isShared = sharedRoutes.some((r) => r.test(pathname));
      const allowedForRole = roleAllowed[role] || [];
      const isAllowed = isShared || allowedForRole.some((r) => r.test(pathname)) || pathname === "/";
      if (!isAllowed) {
        setLocation("/");
      }
    } catch (error) {
      console.error("Failed to switch role:", error);
      alert("Failed to switch role. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <Button
        variant="outline"
        disabled
        className="flex items-center space-x-2"
      >
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
        <span>Loading...</span>
      </Button>
    );
  }

  if (!user) {
    return (
      <Button
        variant="outline"
        disabled
        className="flex items-center space-x-2"
      >
        <span>No User</span>
      </Button>
    );
  }

  const roleConfig: Record<Role, { label: string; icon: any; description: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    superadmin: {
      label: "SuperAdmin Portal",
      icon: Shield,
      description: "System administration",
      variant: "destructive" as const,
    },
    broker: {
      label: "Broker Portal",
      icon: Building2,
      description: "Manage clients and prospects",
      variant: "default" as const,
    },
    employer: {
      label: "Employer Portal",
      icon: Users,
      description: "HR and employee management",
      variant: "secondary" as const,
    },
    employee: {
      label: "Employee Portal",
      icon: User,
      description: "Personal benefits management",
      variant: "outline" as const,
    },
  };

  const CurrentIcon = roleConfig[currentRole]?.icon || Building2;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center space-x-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          data-testid="role-switcher-trigger"
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="text-sm font-medium">
            {roleConfig[currentRole as keyof typeof roleConfig]?.label ||
              "Broker Portal"}
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Switch Portal</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {Object.entries(roleConfig)
          .filter(([role]) => !allowedRoles || allowedRoles.includes(role as Role))
          .map(([role, config]) => {
          const Icon = config.icon;
          const isActive = currentRole === (role as Role);

          return (
            <DropdownMenuItem
              key={role}
              onClick={() => !isActive && switchRole(role as Role)}
              className={`flex items-center space-x-3 cursor-pointer ${isActive ? "bg-muted" : ""}`}
              data-testid={`role-switcher-option-${role}`}
            >
              <Icon className="h-4 w-4" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{config.label}</span>
                  {isActive && (
                    <Badge variant={config.variant} className="text-xs">
                      Current
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {config.description}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-xs text-muted-foreground">
          Current User: {user?.email || "Unknown User"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
